<template>
  <b-container class="referral-jam-event-room">
    <div class="d-flex justify-content-center align-items-center my-5" v-if="state === 'LOADING'">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <div class="d-flex justify-content-center align-items-center my-5" v-else-if="state === 'OFFLINE'">
      <p>{{ $t('referral-jam.errors.event-offline') }}</p>
    </div>
    <div class="d-flex justify-content-center align-items-center my-5"  v-else-if="state === 'NOT-FOUND'">
      <p>{{ $t('referral-jam.errors.event-not-found') }}</p>
    </div>
    <template v-else-if="event && event.id">

      <div class="d-flex flex-row justify-content-start align-items-start my-3 border-bottom pb-3" v-if="event && event.id">
        <img class="event-logo mr-3" v-if="event.branding && event.branding.logo && event.branding.logo.fileLocation"
          :src="event.branding.logo.fileLocation"/>
        <h2 class="m-0">{{ event.title }}</h2>
        <div class="flex-fill"/>
        <b-alert class="m-2 m-md-0" show variant="info" v-if="event.endDate && event.endDate < timeNow">
          <strong>{{ $t('referral-jam.labels.ended-at') }}</strong> {{ event.endDate | formatDate('Do MMMM YYYY, HH:mm') }}
        </b-alert>
        <b-alert class="m-2 m-md-0" show variant="info" v-else-if="event.startDate && event.endDate && event.startDate < timeNow">
          <strong>{{ $t('referral-jam.labels.ends-in') }}</strong> {{ eventEndsIn }}
        </b-alert>
        <b-alert class="d-none d-lg-block m-2 m-md-0" show variant="info" v-else-if="event.startDate > timeNow">
          <strong>{{ $t('referral-jam.labels.starts-in') }}</strong> {{ eventStartsIn }}
        </b-alert>
        <b-dropdown v-if="isRecruiter" no-caret right variant="transparent">
          <template #button-content><font-awesome-icon icon="ellipsis-v"/></template>
          <b-dropdown-item :to="{ name: 'EventPage', params: { eventID: event.id } }">View Page</b-dropdown-item>
        </b-dropdown>
      </div>

      <b-alert variant="warning" show v-if="event.status === 'INACTIVE'">
        <strong>{{ $t('referral-jam.hints.disabled') }}</strong> - <router-link :to="{ name: 'EventSettings', params: { eventID: event.id } }">{{ $t('referral-jam.buttons.settings') }}</router-link>
      </b-alert>
      <b-alert variant="danger" show v-if="event.status === 'DELETED'">
        <strong>{{ $t('referral-jam.hints.deleted') }}</strong> - <router-link :to="{ name: 'EventSettings', params: { eventID: event.id } }">{{ $t('referral-jam.buttons.settings') }}</router-link>
      </b-alert>

      <b-row class="my-3" v-if="event && event.id">
        <b-col cols="12" lg="8">
          <b-row class="my-3">
            <b-col cols="12" md="6">
              <b-card class="my-1" body-class="d-flex flex-row justify-content-center align-items-center p-2" v-if="leaderboardRow">
                <h3>{{ leaderboardRow.total || 0 }} <span class="text-muted">{{ leaderboardRow.total || 0 | plural(leaderboardCurrency, false) }}</span></h3>
              </b-card>
              <b-card class="my-1" body-class="d-flex flex-row justify-content-center align-items-center p-2" v-else>
                <h3 class="text-muted">{{ 0 | plural(leaderboardCurrency, true) }}</h3>
              </b-card>

              <b-card class="my-1" body-class="d-flex flex-row p-0" v-if="leaderboardScores">
                <div class="flex-fill border-right text-center p-2">
                  <h3>{{ leaderboardScores.shares || 0 }} <span class="text-muted">{{ leaderboardScores.shares || 0 | plural('share', false) }}</span></h3>
                </div>
                <div class="flex-fill text-center p-2">
                  <h3>{{ leaderboardScores.views || 0 }} <span class="text-muted">{{ leaderboardScores.views || 0 | plural('view', false) }}</span></h3>
                </div>
              </b-card>
              <b-card class="my-1" body-class="d-flex flex-row p-0" v-else>
                <div class="flex-fill border-right text-center p-2">
                  <h3 class="text-muted">{{ 0 | plural('share') }}</h3>
                </div>
                <div class="flex-fill text-center p-2">
                  <h3 class="text-muted">{{ 0 | plural('referral') }}</h3>
                </div>
              </b-card>
            </b-col>
            <b-col cols="12" md="6">
              <b-media class="mx-3 my-1 align-items-center" v-if="event.leaderboard && event.leaderboard.prizes && event.leaderboard.prizes.first && event.leaderboard.prizes.first.title">
                <template #aside v-if="event.leaderboard.prizes && event.leaderboard.prizes.first && event.leaderboard.prizes.first.image && typeof event.leaderboard.prizes.first.image.fileLocation === 'string'">
                  <b-img v-if="event.leaderboard.prizes.first.image.fileLocation.endsWith('.gif')" class="rounded-circle" height="100" width="100"
                    :src="event.leaderboard.prizes.first.image.fileLocation" :alt="event.leaderboard.prizes.first.title || null"/>
                  <b-img v-else class="rounded-circle" height="100" width="100"
                    :src="event.leaderboard.prizes.first.image.fileLocation | cdn('resize', 256, 256)" :alt="event.leaderboard.prizes.first.title || null"/>
                </template>
                <h4 class="m-0">{{ $t('referral-jam.labels.win') }} <b>{{ event.leaderboard.prizes.first.title }}</b>!</h4>
              </b-media>
            </b-col>
          </b-row>

          <template v-if="isCampaignsEnabled && event.endDate && event.endDate < timeNow">
            <b-card body-class="p-md-5">
              <h2>{{ $t('referral-jam.titles.finished') }}</h2>
              <p>{{ $t('referral-jam.hints.finished') }}</p>
              <b-button variant="primary" @click="$router.push({ name: 'Dashboard' })">{{ $t('referral-jam.buttons.join-campaign') }}</b-button>
            </b-card>
          </template>
          <template v-else>
            <schedule-linkedin-card v-if="canScheduleLinkedIn" class="my-3" scheduleModalID="event-schedule-linkedin">
              <template v-slot:schedule-posts-modal>
                <schedule-posts-modal :eventID="event.id"
                  :jobIDs="Array.isArray(event.jobs) ? event.jobs.map(({ id }) => id) : null"
                  :contentShareIDs="Array.isArray(event.contentShares) ? event.contentShares.map(({ id }) => id) : null"
                  @modal-close="() => $bvModal.hide('event-schedule-linkedin')"/>
              </template>
            </schedule-linkedin-card>

            <!-- <div class="d-flex flex-row d-flex flex-row justify-content-start align-items-center my-3" v-if="event.id === '01FG905KQRVTJ2CSBTZM3CH22Z' || true">
              <b-img class="rounded-circle mr-3" src="https://media-exp1.licdn.com/dms/image/C4E03AQHMGOs_7fKEqw/profile-displayphoto-shrink_200_200/0/1517672109451?e=1632355200&v=beta&t=XzJhSD1j-ulNbstP_jr5Rq96DwuxCDOGiL5rVID1_Jw" width="64" alt="placeholder"/>
              <h5 class="m-0">Barney Mellors</h5>
              <div class="flex-fill"/>
              <b-btn variant="primary"><font-awesome-icon icon="video"/></b-btn>
            </div> -->

            <template v-if="Array.isArray(event.jobs) && event.jobs.length && !isEY">
              <b-card v-for="job in event.jobs" :key="job.id"
                class="p-0 mb-3" body-class="px-3" footer-class="bg-primary-a1 border-0 p-2">
                  <div class="d-flex flex-row justify-content-start align-items-start pb-4">
                    <h3 class="flex-fill m-0" v-b-modal="`description-${job.id}`">{{ job.title }}</h3>
                  </div>

                  <key-stats class="w-100" :job="job"/>

                  <template v-slot:footer>
                    <div class="d-flex flex-row justify-content-end align-items-center">
                      <b-btn v-if="job.description" size="sm" variant="link"
                        v-page-click="{ id: 'descriptionModal', eventID: event.id, jobID: job.id }"
                        v-b-modal="`description-${job.id}`">{{ $t('campaign.buttons.read-description') }}</b-btn>
                      <div class="flex-fill"/>
                      <b-btn v-if="!hideLinkedInSearch" v-b-modal="`job-linkedinSearch-${job.id}`"
                        v-page-click="{ id: 'linkedInSearchModal', eventID: event.id, jobID: job.id }"
                        class="mr-2" variant="linkedin"><font-awesome-icon class="mr-2" :icon="['fab', 'linkedin']"/> {{ $t('jobs.labels.matches') }}</b-btn>
                      <b-btn variant="primary" v-b-modal="`job-actions-${job.id}`"
                        v-page-click="{ id: 'referModal', eventID: event.id, jobID: job.id }">{{ $t('jobs.ctas.item-employee') }}</b-btn>
                    </div>
                  </template>

                  <b-modal :id="`description-${job.id}`" :title="job.title" hide-footer lazy size="lg">
                    <markdown :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
                  </b-modal>
                  <b-modal :id="`contacts-${job.id}`" title="Your Contacts" body-class="py-0" hide-footer size="xl" lazy>
                    <no-ssr>
                      <contacts-browser
                        class="my-3"
                        :eventID="event.id"
                        :jobID="job.id"
                        :jobTitle="job.title"
                        :linkedInSearchURL="job.customFieldsHash && job.customFieldsHash.linkedInSearchURL ? job.customFieldsHash.linkedInSearchURL : null"
                        :shareUrl="createJobShareUrl(job)"
                        :canShareJobsWith="canShareJobsWith"
                        :jobShareText="jobShareText"
                        @trackedLiBrowseEvent="trackedLiBrowseEvent({ jobID: job.id })"
                        @trackShareEvent="({ channel }) => trackJobSharedEvent({ jobID: job.id, channel })"
                        @trackShareClick="confettiRefresh"
                      />
                    </no-ssr>
                  </b-modal>
                  <b-modal :id="`job-linkedinSearch-${job.id}`" :title="$t('campaign.search-linkedin.title')" hide-footer lazy v-if="!hideLinkedInSearch">
                    <linkedin-search-details :jobID="job.id" :jobTitle="job.title" :shareUrl="createJobShareUrl(job)"
                      :linkedInSearchURL="(job.customFieldsHash ? job.customFieldsHash.linkedInSearchURL : null) || job.linkedInSearchURL"
                      :previewText="jobShareText"
                      :previewArgs="{ jobTitle: job.title }"
                      @trackedLiBrowseEvent="trackedLiBrowseEvent({ jobID: job.id })"
                      @trackShareEvent="({ channel }) => trackJobSharedEvent({ jobID: job.id, channel })" />
                  </b-modal>
                  <b-modal :id="`job-actions-${job.id}`" header-class="border-0 pt-2 pb-0" hide-footer lazy>
                    <job-actions-modal :job="job" :event="event" :modalID="`job-actions-${job.id}`" @onCompleted="confettiRefresh"/>
                  </b-modal>
              </b-card>
            </template>

            <template v-if="Array.isArray(event.contentShares) && event.contentShares.length">
              <!-- <div class="d-flex flex-row d-flex flex-row justify-content-start align-items-center my-3">
                <b-img class="rounded-circle mr-3" src="https://media-exp1.licdn.com/dms/image/C4E03AQHMGOs_7fKEqw/profile-displayphoto-shrink_200_200/0/1517672109451?e=1632355200&v=beta&t=XzJhSD1j-ulNbstP_jr5Rq96DwuxCDOGiL5rVID1_Jw" width="64" alt="placeholder"/>
                <h5 class="m-0">Barney Mellors</h5>
                <div class="flex-fill"/>
                <b-btn variant="primary"><font-awesome-icon icon="video"/></b-btn>
              </div> -->

              <b-row class="card-deck">
                <b-col class="mb-3" cols="12" sm="6" v-for="entry in event.contentShares" :key="`item-${entry.id}`">
                  <content-card :entry="entry" :share-url="createContentShareUrl(entry)"
                    @trackShare="data => trackContentSharedEvent(entry, data)"/>
                </b-col>
              </b-row>
            </template>
          </template>
        </b-col>

        <b-col cols="12" lg="4">

          <csr-initiative
            v-if="isCollectiveValid(event.collectiveGoal)"
            :isPreview="false"
            :isCampaign="true"
            :monthlyTotalPoints="totalPoints"
            :csrInitiative="event.collectiveGoal"
            class="my-3"
          />

          <collective-goal
            v-if="isCollectiveGoalTemplateValid(event.collectiveGoal)"
            :progress="collectiveGoalProgress"
            :template="event.collectiveGoal"
            class="my-3"
          />

          <b-card class="my-3" v-if="event.leaderboard && !hideLeaderboard">
            <div class="d-flex flex-row justify-content-between align-items-center" v-if="isRecruiterAdmin">
              <h4 class="text-uppercase m-0">{{ $t('campaign.titles.leaderboard') }}</h4>
              <b-btn class="text-white" size="sm" variant="success" v-b-modal.presentation-mode>
                <font-awesome-icon icon="tv" size="sm"/>
              </b-btn>
            </div>
            <h4 class="text-uppercase" v-else>{{ $t('campaign.titles.leaderboard') }}</h4>

            <leaderboard-list :rows="event.leaderboard.rows"
              :raffleMode="event.leaderboard.raffleMode" :prizes="event.leaderboard.prizes"/>

            <div class="d-flex flex-row justify-content-center align-items-center w-100">
              <b-btn class="text-primary px-0" size="sm" v-b-modal.event-leaderboard-list variant="link">
                View All ({{ event.leaderboard.rows.length }})
              </b-btn>
            </div>
            <b-modal id="event-leaderboard-list" title="Leaderboard" hide-footer lazy size="lg">
              <leaderboard :rows="event.leaderboard.rows" :scores="event.leaderboard.scores" :raffleMode="event.leaderboard.raffleMode" :prizes="event.leaderboard.prizes"/>
            </b-modal>
          </b-card>

          <b-modal v-if="isRecruiterAdmin && event.leaderboard" id="presentation-mode" header-class="align-items-center" hide-footer lazy scrollable
            size="100" v-model="isPresentationMode">
              <template v-slot:modal-header>
                <h5 class="modal-title">{{ event.title }}</h5>
                <div class="flex-fill"/>
                <b-alert class="m-2 m-md-0" show variant="info" v-if="event.endDate && event.endDate < timeNow">
                  <strong>{{ $t('referral-jam.labels.ended-at') }}</strong> {{ event.endDate | formatDate('Do MMMM YYYY, HH:mm') }}
                </b-alert>
                <b-alert class="m-2 m-md-0" show variant="info" v-else-if="event.startDate && event.endDate && event.startDate < timeNow">
                  <strong>{{ $t('referral-jam.labels.ends-in') }}</strong> {{ eventEndsIn }}
                </b-alert>
                <b-alert class="m-2 m-md-0" show variant="info" v-else-if="event.startDate > timeNow">
                  <strong>{{ $t('referral-jam.labels.starts-in') }}</strong> {{ eventStartsIn }}
                </b-alert>
                <b-btn-close @click="() => $bvModal.hide('presentation-mode')"/>
              </template>
              <b-row v-if="event.leaderboard">
                <b-col cols="12" md="6">
                  <csr-initiative
                    v-if="isCollectiveValid(event.collectiveGoal)"
                    :isPreview="false"
                    :isCampaign="true"
                    :monthlyTotalPoints="totalPoints"
                    :csrInitiative="event.collectiveGoal"
                    class="my-3"
                  />
                  <collective-goal
                    v-if="isCollectiveGoalTemplateValid(event.collectiveGoal)"
                    :progress="collectiveGoalProgress"
                    :template="event.collectiveGoal"
                    class="my-3"
                  />
                  <div v-if="!hideLeaderboard" class="d-flex flex-row justify-content-between align-items-center">
                    <h4 class="text-uppercase my-3">{{ $t('campaign.titles.leaderboard') }}</h4>
                    <b-dropdown id="more-leaderboard" right size="sm" text="More" variant="primary" v-if="event.leaderboard.raffleMode || event.leaderboard.spotPrize">
                      <b-dropdown-item v-if="event.leaderboard.raffleMode" :disabled="false && !eventIsActive" v-b-modal.raffle-main-spinner>{{ $t('referral-jam.buttons.draw-prize') }}</b-dropdown-item>
                      <b-dropdown-item v-if="event.leaderboard.spotPrize" :disabled="false && !eventIsActive" v-b-modal.raffle-spot-spinner>{{ $t('referral-jam.buttons.draw-spot-prize') }}</b-dropdown-item>
                    </b-dropdown>
                  </div>
                  <leaderboard v-if="!hideLeaderboard" :rows="event.leaderboard.rows" :scores="event.leaderboard.scores"
                    :raffleMode="event.leaderboard.raffleMode" :prizes="event.leaderboard.prizes"
                    hide-prize-descriptions/>
                </b-col>
                <b-col cols="12" md="6">
                  <h4 class="text-uppercase my-3">{{ $t('campaign.titles.live-feed') }}</h4>
                  <live-feed v-if="Array.isArray(event.leaderboard.history) && event.leaderboard.history.length"
                    :users="event.participants.map(({ user }) => user)" :history="event.leaderboard.history"
                    hide-pagination :limit="100"/>
                  <div v-else class="d-flex flex-column justify-content-center align-items-center my-2 w-100">
                    <p class="m-0">{{ $t('referral-jam.hints.live-feed-empty') }}</p>
                  </div>
                </b-col>
              </b-row>
              <b-modal id="raffle-main-spinner" v-if="event.leaderboard.raffleMode" hide-footer no-body lazy>
                <raffle-spinner class="p-3"
                  :participants="event.participants.map(({ user }) => user)"
                  :totals="event.leaderboard.rows.reduce((l, { id, total }) => ({ ...l, [id]: total }), {})"
                  :previousWinners="Array.isArray(mainPrizeWinners) ? mainPrizeWinners : []"
                  @selected-winner="userID => selectedMainPrizeWinner(userID)"/>
              </b-modal>
              <b-modal id="raffle-spot-spinner" v-if="event.leaderboard.spotPrize" hide-footer no-body lazy>
                <raffle-spinner class="p-3"
                  :participants="event.participants.map(({ user }) => user)"
                  :totals="event.leaderboard.rows.reduce((l, { id, total }) => ({ ...l, [id]: total }), {})"
                  :previousWinners="Array.isArray(spotPrizeWinners) ? spotPrizeWinners : []"
                  @selected-winner="userID => selectedSpotPrizeWinner(userID)"/>
              </b-modal>
            </b-modal>

          <b-card class="my-3" v-if="event.leaderboard">
            <div class="d-flex flex-row justify-content-between align-items-center" v-if="isRecruiterAdmin">
              <h4 class="text-uppercase m-0">{{ $t('campaign.titles.live-feed') }}</h4>
              <b-btn class="text-white" size="sm" variant="success" v-b-modal.presentation-mode>
                <font-awesome-icon icon="tv" size="sm"/>
              </b-btn>
            </div>
            <h4 class="text-uppercase" v-else>{{ $t('campaign.titles.live-feed') }}</h4>

            <template v-if="Array.isArray(event.leaderboard.history) && event.leaderboard.history.length">
              <live-feed hide-pagination stacked
                :users="event.participants.map(({ user }) => user)" :history="event.leaderboard.history.slice(0, 5)"/>
              <div class="d-flex flex-row justify-content-center align-items-center w-100">
                <b-btn class="text-primary" size="sm" variant="link" v-b-modal.live-feed-details>View more</b-btn>
              </div>
              <b-modal id="live-feed-details" title="Live Feed" hide-footer lazy>
                <live-feed :users="event.participants.map(({ user }) => user)" :history="event.leaderboard.history" :limit="50"/>
              </b-modal>
            </template>
            <div v-else class="d-flex flex-column justify-content-center align-items-center my-2 w-100">
              <p class="m-0">{{ $t('referral-jam.hints.live-feed-empty') }}</p>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-modal id="event-rules" title="Event Rules" hide-footer>
        <rich-text-content v-if="event.rulesContent" :value="event.rulesContent"/>
      </b-modal>

      <!-- <b-modal id="event-activity" title="Your Activity">
        <p>Your Activity table coming soon</p>
      </b-modal> -->

    </template>
  </b-container>
</template>

<script>
import _sortBy from 'lodash/sortBy';
import AdvancedForm from '@src/components/AdvancedForm';
import ms from 'ms';
import qs from 'querystring';
import Share from '@/components/Share';
import ContentCard from '@/components/content-share/ContentCard';
import EventRoomQuery from '@/graphql/queries/RfjmEventRoom.gql';
import RfjmAddParticipantsMutation from '@src/graphql/mutations/RfjmAddParticipants.gql';
import CurrentUserEditMutation from '@/graphql/mutations/CurrentUserEdit.gql';
import Markdown from 'vue-markdown';
import KeyStats from '@src/components/jobs/key-stats';
import ContactsBrowser from '@src/modals/event-contacts-browser';
import Leaderboard from '@src/components/referral-jam/Leaderboard';
import LeaderboardList from '@src/components/referral-jam/LeaderboardList';
import LiveFeed from '@src/components/referral-jam/LiveFeed';
import RaffleSpinner from '@src/components/leaderboards/RaffleSpinner';
import eventsMixin from '@src/mixins/events';
import scheduleLinkedInMixin from '@src/mixins/scheduleLinkedIn';
import LinkedinSearchDetails from '@src/components/jobs/LinkedInSearch';
import JobActionsModal from '@src/modals/job-actions-modal';
import CsrInitiative from '@/components/dashboard/CsrInitiative';
import CollectiveGoal from '@/components/dashboard/collective-goal';
import { RichTextContent } from '@/components/RichTextField';
import { createVueMetaBranding } from '@src/lib/branding';
import { mapRoot } from '@src/mixins/root';

const { default: confetti } = process.env.VUE_ENV === 'client' ? require('canvas-confetti') : null;

export default {
  props: {
    eventID: {
      type: String,
      required: true,
    },
  },
  components: {
    AdvancedForm,
    KeyStats,
    Markdown,
    ContactsBrowser,
    Leaderboard,
    LeaderboardList,
    LiveFeed,
    ContentCard,
    RaffleSpinner,
    Share,
    RichTextContent,
    LinkedinSearchDetails,
    JobActionsModal,
    CsrInitiative,
    CollectiveGoal,
  },
  mixins: [ eventsMixin, scheduleLinkedInMixin ],
  data: () => ({
    isPresentationMode: false,
    // presentationChecksum: null,
    state: null,
    updateInterval: null,
    updateRefreshRate: '30s',
    mainPrizeWinners: null,
    spotPrizeWinners: null,
  }),
  created() {
    this.state = 'LOADING';
  },
  metaInfo() {
    const head = {};

    if (this.event && this.event.id) {
      const { title } = this.event;
      head.title = title;

      if (this.event.branding) {
        const isAdminUI = process.env.VUE_ENV === 'server' ? false : process.env.ADMIN_UI;
        const nonce = process.env.VUE_ENV === 'server' ? (this.$ssrContext.nonce || '') : '';
        createVueMetaBranding.call(this, head, this.event.branding, { isAdminUI, nonce });
      }
    }

    return head;
  },
  async mounted() {
    this.startUpdates();
    if (this.$route.query && this.$route.query.callback) {
      try {
        switch (this.$route.query.callback) {
          case 'connectedLinkedInForScheduling': {
            this.connectedLinkedInForScheduling();
            this.$bvModal.show('event-schedule-linkedin');
          }
        }
      } catch (err) {
        this.$log.error(err);
      }
    }
  },
  beforeDestroy() {
    this.stopUpdates();
  },
  apollo: {
    event: {
      query: EventRoomQuery,
      variables() {
        const eventID = this.eventID.split('-').pop();
        return { eventID, isRecruiter: this.isRecruiter };
      },
      update(data) {
        const { company } = data || {};
        const { event } = company || {};

        console.log({ event });

        const { isCampaignsEnabled, timeNow } = this;
        const { endDate, preferences } = event || {};
        const { redirectAfterFinished } = preferences || {};
        if (isCampaignsEnabled && redirectAfterFinished && endDate && endDate && endDate < timeNow) {
          this.$router.push({ name: 'Dashboard' });
        }

        if (event.leaderboard) {
          const { isEY } = this;
          event.leaderboard.rows = (Array.isArray(event.participants) ? event.participants : []).map(({ user }) => {
            const { history, scores } = event.leaderboard;
            const { id, fullName: name } = user;

            const scoreIDs = {};
            const events = (Array.isArray(history) ? history : [])
              .reduce((rollup, row) => {
                let scoreID = null;
                if (row && row.userID === id && row.eventName) {
                  const { eventName, jobID, contentShareID } = row;
                  const { weeklyMaximum, value, maximum } = scores.find(({ eventNames }) => eventNames.includes(eventName)) || {};

                  // EY: only count the actions that are set to be counted
                  if (weeklyMaximum && isEY && (rollup[eventName] || 0) >= 2 * (weeklyMaximum / value)) {
                    return rollup;
                  }

                  if (jobID || contentShareID) {
                    scoreID = jobID ? `JOB_${jobID}` : `CONTENT_${contentShareID}`;
                    scoreIDs[scoreID] = (scoreIDs[scoreID] || 0) + 1;
                  }

                  if (!scoreID || !maximum || (maximum && scoreID && scoreIDs[scoreID] <= maximum)) {
                    rollup[eventName] = (rollup[eventName] || 0) + 1;
                  }
                }
                return rollup;
              }, {});

            const total = Object.keys(events).reduce((t, eventName) => {
              const value = (Array.isArray(scores) ? scores : []).reduce((t, { eventNames, disabled, value }) => {
                if (Array.isArray(eventNames) && eventNames.includes(eventName) && !disabled && value > 0) {
                  return t + value;
                } else {
                  return t;
                }
              }, 0);
              return t + (value * (events[eventName] || 0));
            }, 0);

            return { id, name, ...events, total };
          });
          event.leaderboard.rows = _sortBy(event.leaderboard.rows, [ 'total' ]).reverse();
          // if (this.isPresentationMode) {
          //   this.presentationChecksum = event.leaderboard.rows.map(({ id, total }) => `${id}-${total}`).join(' ');
          // }
        }

        event.preferences = event.preferences || {};

        return event;
      },
      async result({ loading, data }) {
        if (!loading && data && data.company && data.company.event) {
          if (this.isRecruiterAdmin || this.eventHasStarted) {
            this.state = 'ONLINE';
          } else {
            this.state = 'OFFLINE';
          }

          if (this.eventID !== this.eventHandle) {
            this.$router.replace({
              ...this.$route,
              params: { ...this.$route.params, eventID: this.eventHandle },
            });
          }

          if (this.state === 'ONLINE' && !this.isRecruiterAdmin && !this.eventUserParticipant) {
            await this.addAsEventParticipant();
          }
        } else if (!loading && data && data.company) {
          this.state = 'NOT-FOUND';
        }

        if ([ 'OFFLINE', 'NOT-FOUND' ].includes(this.state)) {
          this.$router.replace({ ...this.$route, name: 'EventPage' });
        }
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
    // leaderboard: {
    //   query: LeaderboardShowQuery,
    //   variables() {

    //   }
    // }
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter', 'isRecruiterAdmin', 'authServiceOrigin', 'origin', 'company', 'isEY' ]),
    ...mapRoot({
      canShareJobsWith({ company }) {
        const { preferences, sitePreferences, sharePreferences } = company || {};
        const { canShareJobsWith } = { ...preferences, ...sitePreferences, ...sharePreferences };
        return canShareJobsWith;
      },
      jobShareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { preferences: eventPreferences } = this.event || {};
        const { jobShareText } = { ...preferences, ...sitePreferences, ...sharePreferences, ...eventPreferences };
        return jobShareText || 'Are you interested in working with me as a {jobTitle}?';
      },
      hasConnectedLinkedIn() {
        const { state } = this.user || {};
        const { linkedIn } = state || {};
        const { accessToken } = linkedIn || {};
        return Boolean(accessToken);
      },
      canScheduleLinkedIn() {
        const { preferences: eventPreferences } = this.event || {};
        const { canScheduleLinkedIn } = { ...eventPreferences };
        return canScheduleLinkedIn === true;
      },
      hideLinkedInSearch() {
        const { preferences: eventPreferences } = this.event || {};
        const { hideLinkedInSearch } = { ...eventPreferences };
        return hideLinkedInSearch === true;
      },
      hideLeaderboard() {
        const { preferences: eventPreferences } = this.event || {};
        const { hideLeaderboard } = { ...eventPreferences };
        return hideLeaderboard === true;
      },
    }),
    isCampaignsEnabled() {
      const { company } = this;
      const { sitePreferences } = company || {};
      return sitePreferences.isCampaignsEnabled;
    },
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    appendUtmQuery() {
      const { handle } = this.event || {};
      return qs.stringify({
        utm_source: 'real-links-referral-jam',
        utm_medium: 'share',
        utm_campaign: handle,
      });
    },
    leaderboardScoreValues() {
      const { leaderboard } = this.event || {};
      if (leaderboard && Array.isArray(leaderboard.scores)) {
        return leaderboard.scores.reduce((s, { id, value }) => ({ ...s, [id]: value }), {});
      } else {
        return {};
      }
    },
    leaderboardRow() {
      const { event, user } = this;
      if (event && event.leaderboard && user && user.id && Array.isArray(event.leaderboard.rows)) {
        return event.leaderboard.rows.find(r => r && r.id === user.id);
      } else {
        return null;
      }
    },
    leaderboardScores() {
      const { event } = this;
      if (this.leaderboardRow && event && event.leaderboard && Array.isArray(event.leaderboard.scores)) {
        return event.leaderboard.scores.reduce((map, col) => {
          if (col.id && Array.isArray(col.eventNames) && col.eventNames.length && !col.disabled) {
            map[col.id] = col.eventNames.reduce((t, eventName) => t + (this.leaderboardRow[eventName] || 0), 0);
          }
          return map;
        }, {});
      } else {
        return null;
      }
    },
    leaderboardCurrency() {
      const { leaderboard } = this.event || {};
      if (leaderboard) {
        return leaderboard.isRaffleMode ? 'ticket' : 'point';
      } else {
        return null;
      }
    },
    connectLinkedInURL() {
      return `${this.authServiceOrigin}/oauth2/linkedin?${qs.stringify({
        scope: 'r_liteprofile r_emailaddress w_member_social',
        redirect: `${this.origin}${this.$route.path}?callback=connectedLinkedInForScheduling`,
      })}`;
    },
    totalPoints() {
      const { leaderboard } = this.event;
      const { history, scores } = leaderboard || {};
      if (Array.isArray(scores) && scores.length && Array.isArray(history) && history.length) {
        return history.reduce((sum, el) => {
          if (!el.eventName) return 0;
          const { value } = scores.find(({ eventNames }) => Array.isArray(eventNames) && eventNames.includes(el.eventName)) || {};
          return sum + (value || 0);
        }, 0);
      }
      return 0;
    },
    collectiveGoalProgress() {
      const { leaderboard, collectiveGoal } = this.event;
      if (!collectiveGoal || !leaderboard) return 0;
      const { history } = leaderboard;
      const { eventNames, conversion } = collectiveGoal;
      console.log({ conversion });
      if (Array.isArray(eventNames) && eventNames.length && Array.isArray(history) && history.length) {
        return Math.round(history.reduce((sum, el) => {
          if (!el.eventName) return sum;
          if (eventNames.includes(el.eventName)) return sum + (1 / conversion);
          return sum;
        }, 0));
      }
      return 0;
    },
  },
  methods: {
    confettiRefresh() {
      confetti({ particleCount: 150, spread: 180, zIndex: 1200 });
      this.$apollo.queries.event.refetch();
    },
    createJobShareUrl({ shareCode }) {
      const { id: eventID } = this.event || {};
      return `${this.origin}/e/r/${eventID}/${shareCode}`;
    },
    createContentShareUrl({ shareCode }) {
      const { id: eventID } = this.event || {};
      return `${this.origin}/e/c/${eventID}/${shareCode}`;
    },
    async addAsEventParticipant() {
      try {
        const { id: eventID } = this.event || {};
        this.$assert(eventID, 404, 'Event not found');
        this.$assert(this.user && this.user.id, 401, 'You are not signed-in');

        const { errors } = await this.$apollo.mutate({
          mutation: RfjmAddParticipantsMutation,
          variables: { event: { id: eventID }, users: { self: true } },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('RfjmAddParticipantsMutation', err));
          throw errors.shift();
        }

        // Reload the participants list!
        await this.$apollo.queries.event.refetch();
      } catch (err) {
        this.$notifyErr(err);
      }
    },
    async trackJobSharedEvent({ jobID, channel }) {
      try {
        const { id: eventID } = this.event;
        await this.$root.captureLog({
          eventName: 'JOB_SHARED',
          event: { id: eventID },
          job: { id: jobID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }

      this.confettiRefresh();
    },
    async trackContentSharedEvent({ id: contentShareID }, { channel }) {
      try {
        const { id: eventID } = this.event;
        await this.$root.captureLog({
          eventName: 'CONTENT_SHARED',
          event: { id: eventID },
          contentShare: { id: contentShareID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }

      this.confettiRefresh();
    },
    async trackedLiBrowseEvent({ jobID }) {
      try {
        const { id: eventID } = this.event;
        await this.$root.captureLog({
          eventName: 'JOB_BROWSE_LINKEDIN',
          event: { id: eventID },
          job: { id: jobID },
          user: { self: true },
        });
      } catch (err) {
        this.$log.error(err);
      }

      this.confettiRefresh();
    },
    async selectedMainPrizeWinner(userID) {
      try {
        this.$assert(!Array.isArray(this.mainPrizeWinners) || !this.mainPrizeWinners.includes(userID),
          'Did not expect mainPrizeWinners to have this userID already', { list: this.mainPrizeWinners, userID });

        this.mainPrizeWinners = (Array.isArray(this.mainPrizeWinners) ? this.mainPrizeWinners : []).concat(userID);
        this.$log.debug(JSON.stringify(this.mainPrizeWinners));

        const { id: eventID } = this.event || {};
        await this.$root.captureLog({
          eventName: 'EVENT_PRIZE_WINNER',
          event: { id: eventID },
          user: { id: userID },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    async selectedSpotPrizeWinner(userID) {
      try {
        this.$assert(!Array.isArray(this.spotPrizeWinners) || !this.spotPrizeWinners.includes(userID),
          'Did not expect spotPrizeWinners to have this userID already', { list: this.spotPrizeWinners, userID });

        this.spotPrizeWinners = (Array.isArray(this.spotPrizeWinners) ? this.spotPrizeWinners : []).concat(userID);
        this.$log.debug(JSON.stringify(this.spotPrizeWinners));

        const { id: eventID } = this.event || {};
        await this.$root.captureLog({
          eventName: 'EVENT_SPOT_PRIZE_WINNER',
          event: { id: eventID },
          user: { id: userID },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    startUpdates() {
      this.updateInterval && clearInterval(this.updateInterval);
      this.updateInterval = setInterval(() => this.$apollo.queries.event.refresh(), ms(this.updateRefreshRate));
    },
    stopUpdates() {
      this.updateInterval && clearInterval(this.updateInterval);
    },
    async connectedLinkedInForScheduling() {
      const response = typeof this.$route.hash === 'string' ? qs.parse(this.$route.hash.substr(1)) : null;
      this.$log.debug(response);

      const {
        access_token: accessToken,
        expires_at: expiresAt,
        expires_in: expiresIn,
        id: linkedInUserID,
        name: linkedInName,
        email: linkedInEmail,
        scope,
      } = response;

      this.$assert(accessToken, 'Missing accessToken from LinkedIn sign-in');
      this.$assert(linkedInUserID, 'Missing id from LinkedIn sign-in');

      const { id: userID } = this.user;
      const update = {
        state: {
          linkedIn: {
            accessToken,
            expiresAt,
            expiresIn,
            scope,
            id: linkedInUserID,
            name: linkedInName,
            email: linkedInEmail,
          },
        },
      };

      this.$log.debug({ update });

      await this.$apollo.mutate({
        mutation: CurrentUserEditMutation,
        variables: { userID, update },
      });

      // Clear callback query & auth details from URL
      this.$router.replace({ ...this.$route, query: null, hash: '' });

      this.$bvModal.show('schedule-posts');
    },
    isCollectiveValid(collectiveGoal) {
      return collectiveGoal && collectiveGoal.description && collectiveGoal.headline && collectiveGoal.icon && collectiveGoal.units && collectiveGoal.status === 'ACTIVE';
    },
    isCollectiveGoalTemplateValid(collectiveGoal) {
      return collectiveGoal && collectiveGoal.description && collectiveGoal.headline && collectiveGoal.icon && collectiveGoal.goal;
    },
  },
  watch: {
    // presentationChecksum(value) {
    //   if (this.isPresentationMode) {
    //     confetti({ particleCount: 400, gravity: 0.8, spread: 180, zIndex: 1200 });
    //   }
    // },
    isPresentationMode(value) {
      this.updateRefreshRate = value ? '5s' : '30s';
      this.startUpdates();
    },
  },
};
</script>

<style lang="scss">
.referral-jam-event-room {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ReferralJam/EventRoom.vue